export type TimelineItem = {
  date: string;
  title: string;
  detail: string;
  side: string;
};

export type EvidenceItem = {
  label: string;
  value: string;
  note: string;
};

export type TopicFaq = {
  q: string;
  a: string;
};

export type Topic = {
  id: string;
  slug: string;
  category: string;
  title: string;
  summary: string;
  cover: string;
  date: string;
  readTime: string;
  heat: number;
  status: string;
  tags: string[];
  keyPoints: string[];
  conclusion: string;
  timeline: TimelineItem[];
  evidence: EvidenceItem[];
  faqs: TopicFaq[];
  relatedIds: string[];
};

export const topics: Topic[] = [
  {
    id: "1024",
    slug: "contract-dispute",
    category: "音乐",
    title: "选秀歌手合约风波：解约函、聊天记录与录音棚的三份材料",
    summary:
      "围绕歌手林知野与原厂牌的解约争议，双方说法长期对立。本篇按时间顺序排列合同、沟通记录与录音棚材料三类线索，把争议焦点逐条拆开。",
    cover:
      "https://readdy.ai/api/search-image?query=Moody%20recording%20studio%20interior%20with%20vintage%20microphone%20and%20scattered%20contract%20papers%20on%20a%20wooden%20desk%2C%20warm%20amber%20lamp%20light%2C%20cinematic%20editorial%20photography%2C%20shallow%20depth%20of%20field%2C%20rich%20shadows%2C%20muted%20crimson%20accents%2C%20high%20detail%2C%20no%20people&width=1200&height=800&seq=guada-topic-1024&orientation=landscape",
    date: "2026-09-06",
    readTime: "9 分钟",
    heat: 9860,
    status: "实锤",
    tags: ["合约", "解约函", "录音棚", "证据链"],
    keyPoints: [
      "解约函的签署时间早于双方公开声明，说明谈判期实际长达两个月。",
      "录音棚排期表与酬劳结算单在时间上相互印证，可作为工作关系的直接佐证。",
      "争议核心并非是否合作，而是合作期间追加条款是否经过双方书面确认。",
    ],
    conclusion:
      "现有材料能够支撑「合作关系真实存在且持续约 14 个月」这一事实，属于可验证范围。至于追加条款是否生效，因缺少双方共同签署的补充文本，仍属于待核实部分，我们不对此下最终判断。",
    timeline: [
      {
        date: "2024-11-03",
        title: "首次合作录制 Demo",
        detail: "双方通过共同好友介绍，在城东一间独立录音棚完成首支 Demo 录制，费用由厂牌先行垫付。",
        side: "双方均确认",
      },
      {
        date: "2025-01-18",
        title: "签署首份合作协议",
        detail: "协议约定为期一年，包含作品代理与演出安排两项内容，未涉及追加分成条款。",
        side: "有文本材料",
      },
      {
        date: "2025-06-22",
        title: "厂牌提出追加分成条款",
        detail: "厂牌方向艺人提出提高后期分成比例的口头方案，艺人未在当期给出书面回应。",
        side: "双方说法一致",
      },
      {
        date: "2025-09-30",
        title: "排期与结算出现分歧",
        detail: "录音棚排期表显示艺人当月有六次进棚记录，但结算单仅记录四次，差额部分成为争议起点。",
        side: "存在材料差异",
      },
      {
        date: "2026-02-11",
        title: "艺人发出解约函",
        detail: "艺人以「条款未经确认」为由发出解约函，函件日期早于双方对外声明约两周。",
        side: "有函件原件",
      },
      {
        date: "2026-02-26",
        title: "双方先后发布公开声明",
        detail: "厂牌方称解约无效，艺人方称协议未生效，两份声明对同一时间节点的表述出现明显出入。",
        side: "各自公开声明",
      },
    ],
    evidence: [
      {
        label: "协议文本",
        value: "首份协议 1 份",
        note: "签署日期清晰，条款中不含追加分成内容，可与后续口头方案形成对照。",
      },
      {
        label: "解约函",
        value: "函件原件 1 份",
        note: "落款日期早于公开声明，是判断谈判周期长度的关键时间锚点。",
      },
      {
        label: "录音棚排期表",
        value: "记录 6 次进棚",
        note: "与结算单记录存在两次差额，差额部分是否计酬尚无书面依据。",
      },
      {
        label: "沟通记录",
        value: "文字记录 3 段",
        note: "其中一段明确提到「条款再议」，但未写明最终结论，属于间接材料。",
      },
    ],
    faqs: [
      {
        q: "为什么这篇会标注为「实锤」而不是「待跟进」？",
        a: "因为「合作关系真实存在且持续约 14 个月」这一点，有协议文本、录音棚记录两类独立材料相互印证，属于可以复核的事实。而条款是否生效属于另一层问题，我们在结论里单独区分了。",
      },
      {
        q: "同一时间点两边说法不一样，你们怎么处理？",
        a: "我们会把双方说法并列写在同一段时间线里，并标注「存在材料差异」，不替任何一方做选择性取舍。读者可以自己判断哪一方的表述更贴近材料。",
      },
      {
        q: "如果后续出现补充协议原件，这篇会更新吗？",
        a: "会。届时我们会在时间线中插入新的节点，并在文末标注修订时间与修订原因，旧结论保留可对比，不会直接覆盖。",
      },
    ],
    relatedIds: ["1020", "1023"],
  },
  {
    id: "1023",
    slug: "variety-editing",
    category: "综艺",
    title: "《回声营地》剪辑争议：被删掉的那 12 秒到底拍了什么",
    summary:
      "一段正片片段引发「断章取义」质疑，节目组释出完整素材后舆论迅速反转。这篇把正片、素材与现场记录三方对照，还原剪辑争议的全过程。",
    cover:
      "https://readdy.ai/api/search-image?query=Television%20editing%20suite%20with%20multiple%20monitors%20showing%20film%20timeline%20frames%2C%20warm%20amber%20and%20orange%20room%20lighting%2C%20cinematic%20editorial%20photography%2C%20focused%20atmosphere%2C%20shallow%20depth%20of%20field%2C%20rich%20shadows%2C%20high%20detail%2C%20no%20people&width=1200&height=800&seq=guada-topic-1023&orientation=landscape",
    date: "2026-09-03",
    readTime: "7 分钟",
    heat: 8420,
    status: "反转",
    tags: ["综艺", "剪辑", "素材", "现场还原"],
    keyPoints: [
      "正片流失的 12 秒恰好包含一段解释性对话，缺失后语境发生改变。",
      "完整素材释出后，此前被指「态度强硬」的一方实际先提出了让步方案。",
      "争议的根源是综艺时长压缩，而非刻意引导，但节目组对素材公开节奏负有责任。",
    ],
    conclusion:
      "这是一个典型的「信息不完整导致的误读」。完整素材足以推翻此前流传的主流说法，因此标注为反转。同时需要说明，节目组的素材释出节奏偏慢，客观上延长了误读的传播周期。",
    timeline: [
      {
        date: "2026-08-15",
        title: "正片首播引发讨论",
        detail: "正片中一段 12 秒的对话被压缩，观众依据剩余片段推测当事嘉宾态度强硬。",
        side: "节目正片",
      },
      {
        date: "2026-08-16",
        title: "相关片段被大量二次传播",
        detail: "多个二创账号截取该片段并配以解读文案，讨论迅速从节目延伸到嘉宾个人。",
        side: "二次传播",
      },
      {
        date: "2026-08-19",
        title: "嘉宾工作室发布简短回应",
        detail: "回应称「现场情况与呈现有出入」，但未提供具体材料，讨论并未平息。",
        side: "当事方声明",
      },
      {
        date: "2026-08-27",
        title: "节目组释出完整素材",
        detail: "释出的完整素材包含被删减的 12 秒，其中嘉宾先提出了折中方案，语境与正片呈现不同。",
        side: "有完整素材",
      },
      {
        date: "2026-08-29",
        title: "舆论方向发生反转",
        detail: "此前批评该嘉宾的讨论大量转向，关注点转移到综艺时长压缩与素材公开机制上。",
        side: "舆论变化",
      },
    ],
    evidence: [
      {
        label: "节目正片",
        value: "删减后片段",
        note: "缺失的 12 秒中包含关键解释句，单独看剩余内容容易产生误读。",
      },
      {
        label: "完整素材",
        value: "未剪辑版本",
        note: "与正片对照后可确认删减位置与内容，是本次反转的核心材料。",
      },
      {
        label: "现场记录",
        value: "场记单 1 份",
        note: "场记单记录的顺序与完整素材一致，可排除后期拼接导致的顺序错位。",
      },
    ],
    faqs: [
      {
        q: "这种反转案例，责任应该算在谁头上？",
        a: "材料显示剪辑本身是时长压缩的结果，没有证据指向刻意引导；但素材公开节奏偏慢，客观上放大了误读。我们把两部分分开陈述，不做单一归因。",
      },
      {
        q: "为什么二创解读不能作为证据？",
        a: "二创内容是对原始素材的二次加工，带有解读者的主观判断，无法作为事实本身。我们只把原始素材、场记单这类一手材料计入证据清单。",
      },
    ],
    relatedIds: ["1024", "1022"],
  },
  {
    id: "1022",
    slug: "casting-rumor",
    category: "影视",
    title: "换角传闻复盘：一份未署名通告如何演变成全网热搜",
    summary:
      "一张没有抬头与落款的通告截图，让「换角」传闻在两天内冲上热搜。溯源后发现该文件为两年前的旧物料，属于典型的旧料二次传播。",
    cover:
      "https://readdy.ai/api/search-image?query=Film%20clapperboard%20and%20rolled%20script%20pages%20on%20a%20dark%20walnut%20table%20under%20dramatic%20single%20side%20light%2C%20cinematic%20editorial%20still%20life%20photography%2C%20deep%20crimson%20and%20warm%20brown%20tones%2C%20rich%20contrast%2C%20high%20detail%2C%20no%20people&width=1200&height=800&seq=guada-topic-1022&orientation=landscape",
    date: "2026-08-30",
    readTime: "6 分钟",
    heat: 7210,
    status: "辟谣",
    tags: ["影视", "选角", "旧料", "溯源"],
    keyPoints: [
      "截图中的通告编号可对应到两年前的另一部作品，与当前项目无关。",
      "该文件的排版格式已停用，新版通告不再使用相同模板。",
      "传闻扩散链条完整：从匿名投稿到二创文案，全程没有出现项目方确认。",
    ],
    conclusion:
      "经与文件编号、模板版本、发布链条三重核对，该通告与当前项目不存在关联，换角说法不成立，标注为辟谣。需要提醒的是，这类无署名文件在传播初期极难自证，读者看到时可以先留意落款与编号。",
    timeline: [
      {
        date: "2026-08-24",
        title: "匿名投稿出现",
        detail: "社交平台出现一张未署名通告截图，内容提到某剧集主要角色调整，未见项目方信息。",
        side: "匿名投稿",
      },
      {
        date: "2026-08-25",
        title: "二创文案推动扩散",
        detail: "多个账号在未核实来源的情况下配文解读，话题进入平台热榜前列。",
        side: "二次传播",
      },
      {
        date: "2026-08-26",
        title: "项目方首次否认",
        detail: "项目方回应称未见相关调整，但未说明截图来源，讨论仍在继续。",
        side: "项目方声明",
      },
      {
        date: "2026-08-28",
        title: "溯源核对完成",
        detail: "通告编号对应两年前另一部作品的物料，模板版本也已停用，与当前项目无关联。",
        side: "可复核材料",
      },
    ],
    evidence: [
      {
        label: "通告截图",
        value: "未署名文件",
        note: "无抬头、无落款、无编号规则说明，属于自证能力极弱的材料。",
      },
      {
        label: "编号比对",
        value: "对应旧项目",
        note: "编号规则可回溯到两年前的物料体系，是本次辟谣的关键依据。",
      },
      {
        label: "模板版本",
        value: "已停用格式",
        note: "该排版格式在新版通告中不再使用，进一步佐证文件为旧物料。",
      },
    ],
    faqs: [
      {
        q: "以后看到类似截图，普通人怎么快速判断？",
        a: "先看三件事：有没有抬头和落款、编号是否能对应到具体项目、排版格式是否与近期物料一致。三点里缺两点以上，基本可以判定需要谨慎对待。",
      },
      {
        q: "为什么辟谣之后热度还是很高？",
        a: "辟谣内容的传播速度通常慢于爆料，这是信息传播的结构性问题。我们做复盘的意义之一，就是把澄清写得同样清晰易读，让读者更愿意转发。",
      },
    ],
    relatedIds: ["1023", "1021"],
  },
  {
    id: "1021",
    slug: "co-brand-sales",
    category: "商业合约",
    title: "联名款「秒罄」争议：销量截图背后的统计口径问题",
    summary:
      "品牌方公布的「三分钟售罄」与平台页面显示的库存数据出现出入，引发数据造假质疑。核对后发现分歧源于统计口径不同，而非数据虚构。",
    cover:
      "https://readdy.ai/api/search-image?query=Neatly%20arranged%20limited%20edition%20product%20boxes%20and%20shopping%20bags%20on%20a%20cream%20studio%20table%2C%20soft%20diffused%20daylight%2C%20minimal%20editorial%20still%20life%20photography%2C%20warm%20neutral%20background%2C%20subtle%20red%20ribbon%20accents%2C%20high%20detail%2C%20no%20people&width=1200&height=800&seq=guada-topic-1021&orientation=landscape",
    date: "2026-08-26",
    readTime: "8 分钟",
    heat: 6540,
    status: "反转",
    tags: ["联名", "销量", "口径", "核实"],
    keyPoints: [
      "品牌口径把预售锁定订单计入首轮库存，平台页面只统计已支付订单。",
      "两个口径的数据本身都没有错，问题出在宣传文案没有说明统计范围。",
      "争议的真正启示是：看到「售罄」时应先确认它指的是哪一层库存。",
    ],
    conclusion:
      "数据本身并未虚构，分歧来自统计口径与文案表述的不严谨。因为「数据造假」这一主流质疑被推翻，本篇标注为反转；但我们仍在结论中指出宣传措辞需要更清晰。",
    timeline: [
      {
        date: "2026-08-19",
        title: "联名款首轮开售",
        detail: "品牌方在社交平台发布「三分钟售罄」海报，配图为后台销量截图。",
        side: "品牌方发布",
      },
      {
        date: "2026-08-20",
        title: "网友质疑数据不符",
        detail: "有用户对比平台商品页，发现部分规格仍显示可购买，质疑销量数据夸大。",
        side: "用户质疑",
      },
      {
        date: "2026-08-22",
        title: "平台侧说明口径差异",
        detail: "平台说明商品页库存仅统计已支付订单，预售锁定的订单不计入该页面。",
        side: "平台说明",
      },
      {
        date: "2026-08-24",
        title: "品牌方补充说明",
        detail: "品牌方公布预售锁定与已支付两套数据，两套数据分别自洽，口径差异得到解释。",
        side: "双方材料对照",
      },
    ],
    evidence: [
      {
        label: "品牌后台截图",
        value: "预售锁定口径",
        note: "包含预售锁定订单，因此首轮即显示售罄，与平台页面口径不同。",
      },
      {
        label: "平台商品页",
        value: "已支付口径",
        note: "仅统计已支付订单，所以部分规格在开售初期仍显示可购买。",
      },
      {
        label: "宣传文案",
        value: "未标注统计范围",
        note: "文案未说明数据对应的库存层级，是本次误解的直接触发点。",
      },
    ],
    faqs: [
      {
        q: "那这次到底算不算虚假宣传？",
        a: "从材料看，数据本身有对应口径支撑，不构成数据虚构；但文案没有标明统计范围，容易造成误解。我们在结论里把这层区分写清楚了，不做非黑即白的判断。",
      },
      {
        q: "为什么把这篇标成反转而不是实锤？",
        a: "因为被广泛传播的主流质疑是「数据造假」，而这一说法被材料推翻，符合反转的定义。至于口径说明是否充分，属于另一个层面的问题。",
      },
    ],
    relatedIds: ["1022", "1019"],
  },
  {
    id: "1020",
    slug: "music-sampling",
    category: "音乐",
    title: "一段 8 秒采样引发的三方版权拉锯",
    summary:
      "一首热门单曲被指采样了另一首老歌，涉及原唱、词曲作者与采样方三方。目前公开材料只能确认相似度，无法确认授权链路是否完整。",
    cover:
      "https://readdy.ai/api/search-image?query=Vintage%20vinyl%20records%20and%20studio%20headphones%20on%20a%20textured%20concrete%20surface%20under%20warm%20spot%20lighting%2C%20cinematic%20editorial%20still%20life%20photography%2C%20deep%20shadows%2C%20amber%20and%20deep%20green%20accents%2C%20high%20detail%2C%20no%20people&width=1200&height=800&seq=guada-topic-1020&orientation=landscape",
    date: "2026-08-21",
    readTime: "7 分钟",
    heat: 5120,
    status: "待跟进",
    tags: ["采样", "版权", "授权", "待跟进"],
    keyPoints: [
      "两段音频的波形与音高结构相似度较高，这一点已被多方承认。",
      "争议不在相似度，而在采样方是否取得完整授权链条。",
      "授权文件涉及三方之间的双边协议，目前公开信息不足以还原全链路。",
    ],
    conclusion:
      "已能确认的只有音频相似度这一层，授权链路因涉及未公开的商业协议而无法核实。因此本篇保留「待跟进」状态，不做侵权与否的判断，待任何一方公布协议要点后再更新。",
    timeline: [
      {
        date: "2026-08-12",
        title: "网友比对音频相似度",
        detail: "有音乐博主对比两首作品的段落，指出存在约 8 秒的相似片段。",
        side: "技术比对",
      },
      {
        date: "2026-08-14",
        title: "采样方回应称已获授权",
        detail: "采样方表示片段使用前已通过合作方取得许可，但未公布具体协议。",
        side: "当事方声明",
      },
      {
        date: "2026-08-17",
        title: "词曲作者方表示未收到告知",
        detail: "原词曲作者一方称未收到相关通知，与采样方说法出现分歧。",
        side: "双方说法冲突",
      },
      {
        date: "2026-08-19",
        title: "争议进入僵持阶段",
        detail: "三方均未公布完整协议内容，公开可核实的材料停留在音频相似度层面。",
        side: "缺乏可核实材料",
      },
    ],
    evidence: [
      {
        label: "音频比对",
        value: "相似片段约 8 秒",
        note: "波形与音高结构相似度较高，这一点争议各方的说法基本一致。",
      },
      {
        label: "采样方声明",
        value: "称已获授权",
        note: "声明中未附协议要点，无法独立验证授权范围与期限。",
      },
      {
        label: "词曲方回应",
        value: "称未收到告知",
        note: "与采样方说法直接冲突，构成当前最大的核实缺口。",
      },
    ],
    faqs: [
      {
        q: "为什么这篇一直停在「待跟进」？",
        a: "因为可公开核实的材料只到音频相似度这一层，授权属于商业协议内容，未公开我们就不做推断。等任何一方公布协议要点，会立即更新。",
      },
      {
        q: "相似度高就一定是侵权吗？",
        a: "不一定。判定需要结合作品构成、授权范围与使用方式综合判断，仅凭波形相似度无法得出结论，这也是我们不下定论的原因。",
      },
    ],
    relatedIds: ["1024", "1019"],
  },
  {
    id: "1019",
    slug: "livestream-returns",
    category: "网红直播",
    title: "直播退货风波：第三方质检报告如何改变舆论走向",
    summary:
      "一场直播带货引发大规模退货，主播与品牌方就产品责任各执一词。第三方质检报告公布后，责任划分变得清晰，事件随即进入处理阶段。",
    cover:
      "https://readdy.ai/api/search-image?query=Stacked%20brown%20shipping%20parcels%20and%20a%20printed%20quality%20inspection%20report%20on%20a%20light%20wooden%20floor%2C%20soft%20natural%20window%20light%2C%20documentary%20editorial%20photography%2C%20warm%20neutral%20tones%2C%20shallow%20depth%20of%20field%2C%20high%20detail%2C%20no%20people&width=1200&height=800&seq=guada-topic-1019&orientation=landscape",
    date: "2026-08-16",
    readTime: "8 分钟",
    heat: 8930,
    status: "实锤",
    tags: ["直播", "退货", "质检", "责任划分"],
    keyPoints: [
      "第三方质检报告显示，问题集中在某一批次，而非全部产品。",
      "主播方在选品环节未留存完整的批次检测记录，存在流程缺口。",
      "品牌方承担批次质量责任，主播方承担选品审核责任，两者并不互斥。",
    ],
    conclusion:
      "第三方质检报告属于可复核的一手材料，据此可以确认问题批次范围，以及双方各自存在的流程缺口，因此标注为实锤。责任划分上，质量责任与选品审核责任分属两方，不互相抵消。",
    timeline: [
      {
        date: "2026-08-05",
        title: "直播带货后集中退货",
        detail: "直播售出的产品在两日内出现集中退货，买家反映的问题较为一致。",
        side: "消费者反馈",
      },
      {
        date: "2026-08-07",
        title: "主播方回应称批次问题",
        detail: "主播方表示问题出在供应批次，自身选品流程合规，将责任指向品牌方。",
        side: "主播方声明",
      },
      {
        date: "2026-08-09",
        title: "品牌方否认批次问题",
        detail: "品牌方称出厂检测合格，双方说法进入对立状态。",
        side: "品牌方声明",
      },
      {
        date: "2026-08-13",
        title: "第三方质检报告公布",
        detail: "报告确认问题集中在某一批次，同时指出选品环节的检测记录存在缺失。",
        side: "第三方材料",
      },
      {
        date: "2026-08-15",
        title: "双方进入处理阶段",
        detail: "品牌方启动该批次退换，主播方补充选品审核流程，事件进入处理阶段。",
        side: "处理进展",
      },
    ],
    evidence: [
      {
        label: "第三方质检报告",
        value: "覆盖 3 个批次",
        note: "明确指出问题集中于第 2 批次，是本次责任划分的核心依据。",
      },
      {
        label: "出厂检测单",
        value: "品牌方提供",
        note: "检测单显示抽检合格，但抽检比例有限，无法覆盖全部出货。",
      },
      {
        label: "选品记录",
        value: "存在缺失",
        note: "主播方未留存完整批次检测记录，构成流程层面的明确缺口。",
      },
      {
        label: "退货诉求统计",
        value: "集中度较高",
        note: "诉求内容高度一致，可作为问题批次范围的间接佐证。",
      },
    ],
    faqs: [
      {
        q: "如果两边都有责任，为什么还能标成实锤？",
        a: "实锤描述的是「事实可被验证」这件事，而不是「只有一方有责任」。质检报告能够确认批次范围与流程缺口，这正是可验证的事实。",
      },
      {
        q: "第三方报告一定可信吗？",
        a: "我们只采用检测机构信息公开、检测方法与样本可追溯的报告。报告中若未列明检测标准，我们会把它降级为间接材料处理。",
      },
    ],
    relatedIds: ["1021", "1020"],
  },
];

export const hotTopics = [topics[0], topics[5], topics[1], topics[2], topics[3]];